/**
 * File Validation Types
 *
 * Centralized type definitions for file validation system.
 * Provides comprehensive type safety for file upload and validation operations.
 */

/**
 * Supported file types enumeration
 * Maps to MIME types for validation
 */
export enum SupportedFileType {
  // Images
  JPEG = 'image/jpeg',
  PNG = 'image/png',
  GIF = 'image/gif',

  // Documents
  PDF = 'application/pdf',
  TXT = 'text/plain',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/**
 * File categories for organization and UI display
 */
export enum FileCategory {
  IMAGE = 'image',
  DOCUMENT = 'document',
  TEXT = 'text',
  SPREADSHEET = 'spreadsheet',
  UNKNOWN = 'unknown',
}

/**
 * File validation error types
 * Provides specific error categorization for better error handling
 */
export enum FileValidationErrorType {
  SIZE_EXCEEDED = 'SIZE_EXCEEDED',
  UNSUPPORTED_TYPE = 'UNSUPPORTED_TYPE',
  COUNT_EXCEEDED = 'COUNT_EXCEEDED',
  TOTAL_SIZE_EXCEEDED = 'TOTAL_SIZE_EXCEEDED',
  INVALID_FILE = 'INVALID_FILE',
  EMPTY_FILE = 'EMPTY_FILE',
}

/**
 * Individual file validation error
 */
export interface FileValidationError {
  type: FileValidationErrorType;
  message: string;
  fileName?: string;
  actualSize?: number;
  maxSize?: number;
  actualType?: string;
  code: string; // For i18n keys
}

/**
 * File validation result
 * Contains validation status and detailed error information
 */
export interface FileValidationResult {
  isValid: boolean;
  errors: FileValidationError[];
  warnings: string[];
  validFiles: File[];
  totalSize: number;
}

/**
 * File validation options/configuration
 * Allows customization of validation rules per use case
 */
export interface FileValidationOptions {
  maxFileSize?: number;
  maxTotalSize?: number;
  maxFileCount?: number;
  allowedTypes?: SupportedFileType[];
  allowEmptyFiles?: boolean;
  customSizeLimit?: (file: File) => number;
  customTypeCheck?: (file: File) => boolean;
}

/**
 * Multi-file validation context
 * Provides context for validating multiple files together
 */
export interface MultiFileValidationContext {
  existingFiles: File[];
  newFiles: File[];
  options: FileValidationOptions;
}

/**
 * File metadata for enhanced validation and display
 */
export interface FileMetadata {
  file: File;
  category: FileCategory;
  isValid: boolean;
  errors: FileValidationError[];
  formattedSize: string;
  preview?: string; // For image previews
}

/**
 * File size units for formatting
 */
export interface FileSizeFormatOptions {
  precision?: number;
  units?: 'binary' | 'decimal'; // 1024 vs 1000 based
  includeUnit?: boolean;
}